import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api, ApiFailure, ageLabel, fileToBase64,
  TEST_STATUS_LABEL, categoryText,
  type InvestigationDetail, type InvestigationRow, type TestResult, type TestStatus,
} from '../api'
import { CategoryPill } from '../components/PatientCategory'
import { useAuth } from '../auth'

const ACTIVE_STATUSES: TestStatus[] = ['advised', 'sample_taken', 'result_awaited']
const ALL_STATUSES: TestStatus[] = ['advised', 'sample_taken', 'result_awaited', 'result_received']

function fmtDate(iso: string): string {
  return iso.slice(5)
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
function kb(bytes: number | null): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Compute the doctor's at-a-glance flag from a numeric value and range.
 * Free-text values ("Positive") stay unflagged - a wrong guess would be
 * worse than no guess in a clinical setting.
 */
function computeFlag(value: string, low: string, high: string): number | null {
  const v = Number(value); const lo = Number(low); const hi = Number(high)
  if (!Number.isFinite(v)) return null
  if (Number.isFinite(hi) && v > hi) return 1
  if (Number.isFinite(lo) && v < lo) return -1
  if (Number.isFinite(lo) || Number.isFinite(hi)) return 0
  return null
}

function flagLabel(f: number | null): { text: string; className: string } {
  if (f === 1) return { text: 'HIGH', className: 'flag-high' }
  if (f === -1) return { text: 'LOW', className: 'flag-low' }
  if (f === 0) return { text: 'NORMAL', className: 'flag-normal' }
  return { text: '', className: '' }
}

export default function Investigations() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canWriteResults = user?.role === 'admin' || user?.role === 'doctor'
  const [rows, setRows] = useState<InvestigationRow[]>([])
  const [showAll, setShowAll] = useState(false)
  const [opened, setOpened] = useState<InvestigationDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)

  const load = useCallback(async () => {
    setBusy(true); setMsg(null)
    try {
      // The tray is one query per active status when narrowed, or one big pull
      // when showing everything - keeps ordering intuitive either way.
      if (showAll) {
        const r = await api.get<{ investigations: InvestigationRow[] }>('/api/investigations?limit=200')
        setRows(r.investigations)
      } else {
        const results = await Promise.all(
          ACTIVE_STATUSES.map((s) =>
            api.get<{ investigations: InvestigationRow[] }>(`/api/investigations?status=${s}&limit=100`)
          ),
        )
        setRows(results.flatMap((r) => r.investigations))
      }
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not load the tray.' })
    } finally {
      setBusy(false)
    }
  }, [showAll])

  useEffect(() => { void load() }, [load])

  async function step(row: InvestigationRow, status: TestStatus) {
    setBusy(true); setMsg(null)
    try {
      await api.patch<InvestigationDetail>(`/api/investigations/${row.id}/status`, { status })
      await load()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not update.' })
    } finally {
      setBusy(false)
    }
  }

  async function openTest(id: string) {
    try {
      const d = await api.get<InvestigationDetail>(`/api/investigations/${id}`)
      setOpened(d)
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not open.' })
    }
  }

  const buckets = ALL_STATUSES.map((s) => ({ status: s, rows: rows.filter((r) => r.status === s) }))

  return (
    <>
      <div className="page-head">
        <h2>Investigations</h2>
        <p>Every advised test in one queue. Mark the sample taken, log the result, move on.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="panel">
        <div className="records-views">
          <button className={showAll ? 'chip' : 'chip is-on'} onClick={() => setShowAll(false)}>
            Active
          </button>
          <button className={showAll ? 'chip is-on' : 'chip'} onClick={() => setShowAll(true)}>
            All
          </button>
          <span className="spacer" />
          {busy && <span className="panel-note" style={{ margin: 0 }}>Loading…</span>}
        </div>
      </div>

      {buckets.map(({ status, rows: bucket }) =>
        (!showAll && status === 'result_received') || bucket.length === 0
          ? null
          : (
            <div key={status} className="panel">
              <h3>{TEST_STATUS_LABEL[status]} <span className="panel-note" style={{ margin: 0 }}>· {bucket.length}</span></h3>
              <div className="table-wrap">
                <table className="records-table">
                  <thead>
                    <tr>
                      <th>Test</th><th>Patient</th><th>Doctor</th><th className="nowrap">Ordered</th>
                      <th className="num">Results</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="rec-name">{r.testName}</div>
                          {r.urgency !== 'routine' && <span className="pill off" style={{ marginRight: 4 }}>{r.urgency}</span>}
                          {r.instructions && <div className="rec-code">{r.instructions}</div>}
                        </td>
                        <td>
                          <div>{r.patientName} <span className="panel-note">{ageLabel(r as any)}</span></div>
                          <div className="rec-code">{[r.patientCode, r.mobile].filter(Boolean).join(' · ')}</div>
                          <CategoryPill category={r.patientCategory} />
                        </td>
                        <td className="nowrap">{r.doctorName.replace(/^Dr\.?\s*/, 'Dr ')}</td>
                        <td className="nowrap">{fmtDate(r.visitDate)}</td>
                        <td className="num">{r.resultCount}</td>
                        <td className="nowrap">
                          {status === 'advised' && <button className="link" disabled={busy} onClick={() => void step(r, 'sample_taken')}>Sample taken</button>}
                          {status === 'sample_taken' && <button className="link" disabled={busy} onClick={() => void step(r, 'result_awaited')}>Awaiting result</button>}
                          {/* Reception opens the same panel to read results;
                              only the wording changes, because they cannot write. */}
                          <button className="link" onClick={() => void openTest(r.id)}>
                            {status === 'result_received' || !canWriteResults ? 'View' : 'Record result'}
                          </button>
                          <button className="link" onClick={() => navigate(`/visits/${r.visitId}`)}>Open visit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ),
      )}

      {rows.length === 0 && !busy && (
        <div className="panel"><div className="empty">Nothing in the tray.</div></div>
      )}

      {opened && (
        <TestModal
          detail={opened}
          onClose={() => setOpened(null)}
          onSaved={async (nextDetail) => {
            setOpened(nextDetail)
            await load()
          }}
          onError={(text) => setMsg({ kind: 'error', text })}
        />
      )}
    </>
  )
}

interface DraftResult {
  value: string
  unit: string
  refRangeLow: string
  refRangeHigh: string
  labName: string
  notes: string
  file: File | null
}

const BLANK_DRAFT: DraftResult = {
  value: '', unit: '', refRangeLow: '', refRangeHigh: '', labName: '', notes: '', file: null,
}

function TestModal({
  detail, onClose, onSaved, onError,
}: {
  detail: InvestigationDetail
  onClose: () => void
  onSaved: (next: InvestigationDetail) => void | Promise<void>
  onError: (text: string) => void
}) {
  const { user } = useAuth()
  const [draft, setDraft] = useState<DraftResult>({ ...BLANK_DRAFT })
  const [busy, setBusy] = useState(false)

  /* Writing a result is admin/doctor on the server. Reception can still work
     the tray - stepping a test forward is theirs - but the form used to open
     for them and only fail on Save, after they had typed the whole thing. */
  const canWriteResults = user?.role === 'admin' || user?.role === 'doctor'

  const flag = computeFlag(draft.value, draft.refRangeLow, draft.refRangeHigh)
  const flagLbl = flagLabel(flag)

  async function pickFile(evt: ChangeEvent<HTMLInputElement>) {
    const file = evt.target.files?.[0] ?? null
    setDraft((d) => ({ ...d, file }))
  }

  async function save() {
    if (!draft.value.trim() && !draft.file) {
      onError('Add a value or attach a file.')
      return
    }
    setBusy(true)
    try {
      const payload: Record<string, unknown> = {
        value: draft.value || null,
        unit: draft.unit || null,
        refRangeLow: draft.refRangeLow || null,
        refRangeHigh: draft.refRangeHigh || null,
        labName: draft.labName || null,
        notes: draft.notes || null,
        flag,
      }
      if (draft.file) payload.file = await fileToBase64(draft.file)
      const next = await api.post<InvestigationDetail>(`/api/investigations/${detail.test.id}/results`, payload)
      setDraft({ ...BLANK_DRAFT })
      await onSaved(next)
    } catch (err) {
      onError(err instanceof ApiFailure ? err.message : 'Could not save the result.')
    } finally {
      setBusy(false)
    }
  }

  async function deleteResult(r: TestResult) {
    if (!confirm('Delete this result row? The file on disk is kept.')) return
    setBusy(true)
    try {
      await api.del(`/api/investigations/results/${r.id}`)
      const next = await api.get<InvestigationDetail>(`/api/investigations/${detail.test.id}`)
      await onSaved(next)
    } catch (err) {
      onError(err instanceof ApiFailure ? err.message : 'Could not delete.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal test-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{detail.test.testName}</h3>
        <div className="panel-note" style={{ margin: '0 0 12px' }}>
          {detail.test.patientName} · {detail.test.patientCode} · {categoryText(detail.test.patientCategory)} · ordered {fmtDate(detail.test.visitDate)} by {detail.test.doctorName}
        </div>

        <h4 style={{ margin: '8px 0 6px' }}>Recorded results</h4>
        {detail.results.length === 0 && <p className="panel-note">No results yet.</p>}
        {detail.results.map((r) => {
          const rf = flagLabel(r.flag)
          return (
            <div key={r.id} className="result-row">
              <div className="result-value">
                {r.value ?? '—'}{r.unit && <span className="rec-code"> {r.unit}</span>}
                {rf.text && <span className={`pill ${rf.className}`} style={{ marginLeft: 6 }}>{rf.text}</span>}
              </div>
              <div className="rec-code">
                {(r.refRangeLow || r.refRangeHigh) && <>Range: {r.refRangeLow ?? '—'} – {r.refRangeHigh ?? '—'}. </>}
                {r.labName && <>{r.labName}. </>}
                {fmtDateTime(r.measuredAt)}
              </div>
              {r.notes && <div style={{ fontSize: 13 }}>{r.notes}</div>}
              {r.filePath && (
                <div style={{ marginTop: 4 }}>
                  <a href={`/api/investigations/results/${r.id}/file`} target="_blank" rel="noreferrer">
                    Open attachment ({kb(r.fileSizeBytes)})
                  </a>
                </div>
              )}
              {canWriteResults && (
                <div style={{ marginTop: 4 }}>
                  <button className="link" disabled={busy} onClick={() => void deleteResult(r)}>Delete this row</button>
                </div>
              )}
            </div>
          )
        })}

        {!canWriteResults && (
          <p className="panel-note" style={{ marginTop: 12 }}>
            Results are entered by a doctor or an administrator. You can still step this test
            forward from the tray, and the result appears here once it is logged.
          </p>
        )}

        {canWriteResults && (
        <>
        <h4 style={{ margin: '16px 0 6px' }}>Add a result</h4>
        <div className="result-form">
          <div className="field">
            <label htmlFor="rv">Value</label>
            <input id="rv" autoFocus placeholder="e.g. 13.4 or Positive"
              value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="ru">Unit</label>
            <input id="ru" placeholder="g/dL" value={draft.unit}
              onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="rl">Range low</label>
            <input id="rl" value={draft.refRangeLow}
              onChange={(e) => setDraft((d) => ({ ...d, refRangeLow: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="rh">Range high</label>
            <input id="rh" value={draft.refRangeHigh}
              onChange={(e) => setDraft((d) => ({ ...d, refRangeHigh: e.target.value }))} />
          </div>
          <div className="field wide">
            <label htmlFor="rlab">Lab</label>
            <input id="rlab" value={draft.labName}
              onChange={(e) => setDraft((d) => ({ ...d, labName: e.target.value }))} />
          </div>
          <div className="field wide">
            <label htmlFor="rn">Notes</label>
            <input id="rn" value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
          </div>
          <div className="field wide">
            <label htmlFor="rf">Attach report (PDF or image, max 8 MB)</label>
            <input id="rf" type="file" accept=".pdf,image/*" onChange={pickFile} />
            {draft.file && <span className="hint">{draft.file.name} · {kb(draft.file.size)}</span>}
          </div>
        </div>

        {flag !== null && (
          <div className="panel-note" style={{ margin: '8px 0' }}>
            Flag: <span className={`pill ${flagLbl.className}`}>{flagLbl.text}</span>
            {' '}(from value {draft.value} vs range {draft.refRangeLow || '—'}–{draft.refRangeHigh || '—'})
          </div>
        )}
        </>
        )}

        <div className="actions">
          {canWriteResults && (
            <button disabled={busy || (!draft.value.trim() && !draft.file)} onClick={() => void save()}>
              Save result
            </button>
          )}
          <button className="secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
